import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { useRef } from 'react'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiKey, CreateApiKeyResponse } from '@/api/hooks/use-api-keys'
import { CopyButton } from '@/components/ui/copy-button'
import { useAuth } from '@/lib/auth-context'

interface StepApiKeyFullProps {
  onFinish: () => void
}

function useLiveKeyMutation(): ReturnType<typeof useMutation<CreateApiKeyResponse, unknown, void>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient.post<CreateApiKeyResponse>(endpoints.apiKeys.create, {
        name: 'Default',
        scope: 'full',
        type: 'live',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  })
}

function buildCurlSnippet(key: string, email: string): string {
  return `curl -X POST https://api.one-email.com/v1/emails \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "from": "sandbox@onboarding.one-email.com",
    "to": ["${email}"],
    "subject": "Hello!",
    "html": "<p>It works!</p>"
  }'`
}

export function StepApiKeyFull({ onFinish }: StepApiKeyFullProps): React.JSX.Element {
  const { user } = useAuth()

  // Check if a live key already exists -- do NOT create duplicates
  const existingKeys = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => apiClient.get<{ data: ApiKey[] }>(endpoints.apiKeys.list),
  })

  const existingLiveKey = existingKeys.data?.data.find((k) => k.type === 'live')

  const createMutation = useLiveKeyMutation()
  const hasTriggeredCreate = useRef(false)

  // Only create a key if: query succeeded, no live key exists, and we haven't already created one
  const shouldCreate =
    existingKeys.isSuccess &&
    !existingLiveKey &&
    !createMutation.data &&
    !createMutation.isPending &&
    !hasTriggeredCreate.current &&
    !createMutation.isError

  if (shouldCreate) {
    hasTriggeredCreate.current = true
    createMutation.mutate()
  }

  const isPending = createMutation.isPending || existingKeys.isLoading
  // Only use the full key from create response — list endpoint returns truncated hints
  const apiKey = createMutation.data?.key
  const hasExistingKey = !!existingLiveKey && !apiKey
  const userEmail = user?.email ?? 'you@example.com'

  return (
    <div className="rounded-2xl border border-surface-3 bg-surface-0 p-8 lg:p-10 shadow-sm">
      <div className="flex flex-col gap-8">
        {/* Header */}
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-bold text-text-primary">Your API key</h2>
          <p className="text-sm text-text-secondary">Use this to authenticate your API requests.</p>
        </div>

        {/* Key display */}
        {isPending ? (
          <div className="flex items-center gap-3 rounded-xl bg-surface-1 px-5 py-4">
            <Loader2 className="h-4 w-4 animate-spin text-text-tertiary" />
            <span className="text-sm text-text-secondary">Creating your API key...</span>
          </div>
        ) : hasExistingKey ? (
          <div className="flex flex-col gap-3">
            <div className="rounded-xl border border-surface-3 bg-surface-1 px-5 py-4">
              <p className="text-sm text-text-secondary">
                You already have a live API key (<code className="font-mono text-text-primary">{existingLiveKey.key_hint ?? existingLiveKey.key}</code>).
                For security, full keys are only shown once at creation.
              </p>
            </div>
            <p className="text-xs text-text-tertiary">
              You can create new API keys anytime in Settings &rarr; API Keys.
            </p>
          </div>
        ) : apiKey ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3 rounded-xl border border-surface-3 bg-surface-1 px-5 py-4">
              <code className="flex-1 break-all font-mono text-sm text-text-primary">{apiKey}</code>
              <CopyButton value={apiKey} />
            </div>

            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
              <span className="text-sm text-warning">Copy this now — you won't see it again.</span>
            </div>

            <p className="text-xs text-text-tertiary">
              You can always create new API keys in Settings &rarr; API Keys.
            </p>
          </div>
        ) : null}

        {/* Quick test */}
        {apiKey && (
          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold text-text-primary">Quick test</h3>
            <div className="relative rounded-xl border border-surface-3 bg-surface-1 p-5">
              <pre className="overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs leading-relaxed text-text-secondary">
                {buildCurlSnippet(apiKey, userEmail)}
              </pre>
              <div className="absolute right-3 top-3">
                <CopyButton value={buildCurlSnippet(apiKey, userEmail)} />
              </div>
            </div>
          </div>
        )}

        {/* Finish */}
        <button
          type="button"
          onClick={onFinish}
          disabled={isPending}
          className="w-full rounded-lg bg-brand-500 px-5 py-3 text-sm font-semibold text-white
            hover:bg-brand-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Go to dashboard
        </button>
      </div>
    </div>
  )
}
